import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  Phone,
  MessageCircle,
  Heart,
  UserCheck,
  Link2,
  X,
  Send,
  Edit,
  Trash2,
  StickyNote,
  Clock,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import AddressLink from "../../components/AddressLink";
import { formatDateString } from "../../utils";
import { getDepartmentClassLabel } from "../../utils/studentDisplay";
import MobileDetailEditForm from "./MobileDetailEditForm";
import {
  getFaithStateBadgeClass,
  getMobileVisitEditorCardClass,
  getMobileVisitTypeButtonClass,
  getMobileVisitTypeIconSrc,
  getMobileVisitTypeIconWrapperClass,
  MOBILE_DETAIL_ACTION_ROW_CLASS,
  MOBILE_DETAIL_ACTIVITY_CHIP_CLASS,
  MOBILE_DETAIL_CARD_CLASS,
  MOBILE_DETAIL_CANCEL_EDIT_BUTTON_CLASS,
  MOBILE_DETAIL_EMPTY_CARD_CLASS,
  MOBILE_DETAIL_INFO_ROW_CLASS,
  MOBILE_DETAIL_LOG_ITEM_CARD_CLASS,
  MOBILE_DETAIL_SECTION_TITLE_CLASS,
  MOBILE_DETAIL_SUBTITLE_CLASS,
  MOBILE_DETAIL_TIMELINE_LABEL_CLASS,
  MOBILE_DETAIL_VISIT_EDITOR_INPUT_CLASS,
  MOBILE_DETAIL_VISIT_EDITOR_SUBMIT_BUTTON_CLASS,
  MOBILE_DETAIL_VISIT_LOG_ACTION_DELETE_BUTTON_CLASS,
  MOBILE_DETAIL_VISIT_LOG_ACTION_EDIT_BUTTON_CLASS,
  MOBILE_DETAIL_VISIT_SHOW_MORE_BUTTON_CLASS,
  MOBILE_MODAL_HEADER_PRIMARY_BUTTON_CLASS,
  MOBILE_MODAL_HEADER_SECONDARY_BUTTON_CLASS,
} from "./mobileListUtils";

function ExpandableText({ text, maxLines = 2, className = "" }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight, 10);
      const maxHeight = lineHeight * maxLines;
      setNeedsExpand(textRef.current.scrollHeight > maxHeight + 5);
    }
  }, [text, maxLines]);

  return (
    <div className="relative">
      <p
        ref={textRef}
        className={`text-sm font-medium text-gray-700 transition-all duration-300 ${className} ${!isExpanded ? `line-clamp-${maxLines}` : ""}`}
      >
        {text}
      </p>
      {needsExpand && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="text-[10px] font-black text-orange-500 mt-1.5 flex items-center gap-0.5 hover:underline"
        >
          {isExpanded ? (
            <>
              <ChevronUp size={12} /> 접기
            </>
          ) : (
            <>
              <ChevronDown size={12} /> 더보기
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default function MobileDetailView({
  viewingItem,
  setViewingItem,
  onCloseDetail,
  isTeacher,
  isDetailEditing,
  setIsDetailEditing,
  teacherFormData,
  setTeacherFormData,
  formData,
  setFormData,
  departmentOptions,
  isDetailDeptOpen,
  setIsDetailDeptOpen,
  handleDetailSave,
  handleDelete,
  handleImageUpload,
  onTouchStart,
  onTouchMove,
  onModalSwipeEnd,
  getDeptColorClass,
  shouldMaskTeacherInfo,
  showLinkPopup,
  setShowLinkPopup,
  teamMembers,
  linkLoading,
  fetchTeamMembers,
  handleLinkAccount,
  handleUnlinkAccount,
  onOpenCheerModal,
  userProfile,
  logInputType,
  setLogInputType,
  logInputContent,
  setLogInputContent,
  logInputDate,
  setLogInputDate,
  logInputSentiment,
  setLogInputSentiment,
  editingLogId,
  setEditingLogId,
  setIsCreatingLog,
  showAllLogs,
  setShowAllLogs,
  visitLogs,
  attendanceLogs,
  onUpdateLog,
  onDeleteLog,
  onAddLog,
  openAlert,
  openConfirm,
  onNavigateToAttendance,
  icons,
}) {
  if (!viewingItem) return null;

  const item = viewingItem;
  const isItemTeacher = isTeacher || item.isTeacher;
  const closeDetail = () => {
    if (onCloseDetail) onCloseDetail();
    else setViewingItem(null);
    setIsDetailEditing(false);
  };

  const handleBackTap = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    closeDetail();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-40 bg-white flex flex-col animate-slide-left overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={() => onModalSwipeEnd(closeDetail)}
    >
      <div className="flex-shrink-0 flex items-center justify-between px-4 border-b border-gray-100 bg-white z-10 pt-[calc(env(safe-area-inset-top)+36px)] pb-3 min-h-[50px]">
        <button
          onClick={handleBackTap}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={handleBackTap}
          className={MOBILE_MODAL_HEADER_SECONDARY_BUTTON_CLASS}
        >
          뒤로
        </button>
        <h3 className="font-bold text-lg text-gray-900 truncate max-w-[200px]">
          {isDetailEditing ? "정보 수정" : viewingItem.name}
        </h3>
        <button
          onClick={() => {
            if (isDetailEditing) {
              handleDetailSave({ preventDefault: () => {} });
            } else {
              setIsDetailEditing(true);
            }
          }}
          className={MOBILE_MODAL_HEADER_PRIMARY_BUTTON_CLASS}
        >
          {isDetailEditing ? "완료" : "수정"}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-white pb-24">
        {isDetailEditing ? (
          <MobileDetailEditForm
            isItemTeacher={isItemTeacher}
            teacherFormData={teacherFormData}
            setTeacherFormData={setTeacherFormData}
            formData={formData}
            setFormData={setFormData}
            departmentOptions={departmentOptions}
            isDetailDeptOpen={isDetailDeptOpen}
            setIsDetailDeptOpen={setIsDetailDeptOpen}
            handleImageUpload={handleImageUpload}
            handleDelete={handleDelete}
            icons={icons}
          />
        ) : (
          <div className="flex-1 overflow-y-auto bg-[#F2F4F6] pb-4">
            <div className="bg-white px-5 pb-8 pt-4 flex flex-col items-center border-b border-gray-100 rounded-b-[2rem] shadow-sm mb-4">
              <div className="w-24 h-24 rounded-[2rem] overflow-hidden shadow-lg border-[3px] border-white bg-gray-50 mb-4">
                {item.photoUrl ? (
                  <img src={item.photoUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white p-2">
                    <img
                      src={
                        isItemTeacher
                          ? item.gender === "여"
                            ? icons.teacher_female
                            : icons.teacher_male
                          : item.gender === "남"
                            ? "/images/boy_animated_light.png"
                            : "/images/girl_animated_light.png"
                      }
                      alt="Profile"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-1">{item.name}</h2>
              <p className="text-gray-500 font-bold text-sm bg-gray-100 px-3 py-1 rounded-full">
                {isItemTeacher
                  ? `${item.role || "교사"} • ${item.charge || "미정"}`
                  : getDepartmentClassLabel(item) || "-"}
              </p>
              {!isItemTeacher && item.faithState === "초신자" && (
                <span className="mt-2 bg-amber-400 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                  NEW 초신자
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 px-5 mb-4">
              <a
                href={`tel:${item.phone}`}
                className={`py-3.5 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg hover:scale-[0.98] active:scale-[0.95] transition-all duration-200 ${getDeptColorClass(item.grade || item.charge)}`}
              >
                <Phone size={18} fill="currentColor" /> 전화
              </a>
              <a
                href={`sms:${item.phone}`}
                className="py-3.5 rounded-2xl bg-[#FFECA1] text-[#4A3B10] font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-yellow-400/20 hover:bg-[#FFD54F] hover:scale-[0.98] active:scale-[0.95] transition-all duration-200"
              >
                <MessageCircle size={18} /> 문자
              </a>
            </div>

            {isItemTeacher && (
              <div className="px-5 mb-6">
                <button
                  onClick={() => {
                    if (!item.linkedUserId) {
                      openAlert("아직 앱 계정이 연결되지 않은 선생님이에요 🥲", "알림");
                      return;
                    }
                    if (onOpenCheerModal) onOpenCheerModal(item);
                  }}
                  disabled={!item.linkedUserId || (userProfile?.uid && item.linkedUserId === userProfile.uid)}
                  className={`w-full py-3.5 rounded-2xl font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 ${item.linkedUserId && !(userProfile?.uid && item.linkedUserId === userProfile.uid) ? "bg-gradient-to-r from-rose-400 to-pink-500 text-white shadow-rose-200" : "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"}`}
                >
                  <Heart size={18} className={item.linkedUserId ? "fill-white animate-pulse" : ""} />
                  {item.linkedUserId ? "격려 보내기" : "앱 계정 연결 필요"}
                </button>
                {!item.linkedUserId && (
                  <p className="text-center text-xs text-gray-400 mt-2 font-medium">
                    관리자가 앱 계정을 연결하면 격려를 보낼 수 있어요!
                  </p>
                )}
              </div>
            )}

            {isItemTeacher && userProfile?.memberRole === "admin" && (
              <div className="px-5 mb-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-8 h-8 rounded-xl flex items-center justify-center ${item.linkedUserId ? "bg-green-100" : "bg-gray-100"}`}
                      >
                        {item.linkedUserId ? (
                          <UserCheck size={16} className="text-green-600" />
                        ) : (
                          <Link2 size={16} className="text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">앱 계정</p>
                        {item.linkedUserId ? (
                          <p className="text-xs text-green-600">{item.linkedUserName || "연결됨"}</p>
                        ) : (
                          <p className="text-xs text-gray-400">연결 안 됨</p>
                        )}
                      </div>
                    </div>
                    {item.linkedUserId ? (
                      <button
                        onClick={handleUnlinkAccount}
                        className="text-xs text-red-500 hover:text-red-600 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition"
                      >
                        연결 해제
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          fetchTeamMembers();
                          setShowLinkPopup(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-bold px-3 py-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 transition"
                      >
                        계정 연결
                      </button>
                    )}
                  </div>

                  {showLinkPopup && (
                    <div className="mt-4 p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex justify-between items-center mb-3">
                        <p className="text-sm font-bold text-gray-800">멤버 선택</p>
                        <button
                          onClick={() => setShowLinkPopup(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      {linkLoading ? (
                        <p className="text-sm text-gray-400 text-center py-4">로딩 중...</p>
                      ) : teamMembers.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">
                          연결 가능한 멤버가 없습니다
                        </p>
                      ) : (
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {teamMembers.map((member) => (
                            <button
                              key={member.uid}
                              onClick={() => handleLinkAccount(member.uid, member.name)}
                              className="w-full text-left p-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition"
                            >
                              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600">
                                {member.name?.charAt(0) || "?"}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">
                                  {member.name || "이름 없음"}
                                </p>
                                <p className="text-xs text-gray-400">
                                  {member.deptName || "부서 없음"}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="px-5 space-y-4 pb-12">
              <h3 className={MOBILE_DETAIL_SECTION_TITLE_CLASS}>상세 정보</h3>
              <div className={[MOBILE_DETAIL_CARD_CLASS, "space-y-4"].join(" ")}>
                <div className={MOBILE_DETAIL_INFO_ROW_CLASS}>
                  <span className="text-gray-400 font-bold text-sm">학교/직장</span>
                  <span className="text-gray-800 font-bold">
                    {shouldMaskTeacherInfo(item) ? (
                      <span className="blur-[3px] select-none text-gray-300">소중한 정보 보호 중 🔒</span>
                    ) : (
                      item.school || "-"
                    )}
                  </span>
                </div>

                <div className={MOBILE_DETAIL_INFO_ROW_CLASS}>
                  <span className="text-gray-400 font-bold text-sm">생년월일</span>
                  <span className="text-gray-800 font-bold">
                    {shouldMaskTeacherInfo(item) ? (
                      <span className="blur-[3px] select-none text-gray-300">소중한 정보 보호 중 🔒</span>
                    ) : item.birth ? (
                      formatDateString(item.birth)
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className={MOBILE_DETAIL_INFO_ROW_CLASS}>
                  <span className="text-gray-400 font-bold text-sm">연락처</span>
                  <span className="text-gray-800 font-bold">{item.phone || "-"}</span>
                </div>
                {!isItemTeacher && (item.guardian1Name || item.guardian1Phone) && (
                  <div className={MOBILE_DETAIL_INFO_ROW_CLASS}>
                    <span className="text-gray-400 font-bold text-sm">
                      보호자1 {item.guardian1Name ? `(${item.guardian1Name})` : ""}
                    </span>
                    <a
                      href={`tel:${item.guardian1Phone}`}
                      className="text-[#354F38] font-bold underline underline-offset-2"
                    >
                      {item.guardian1Phone || "-"}
                    </a>
                  </div>
                )}
                {!isItemTeacher && (item.guardian2Name || item.guardian2Phone) && (
                  <div className={MOBILE_DETAIL_INFO_ROW_CLASS}>
                    <span className="text-gray-400 font-bold text-sm">
                      보호자2 {item.guardian2Name ? `(${item.guardian2Name})` : ""}
                    </span>
                    <a
                      href={`tel:${item.guardian2Phone}`}
                      className="text-[#354F38] font-bold underline underline-offset-2"
                    >
                      {item.guardian2Phone || "-"}
                    </a>
                  </div>
                )}
                <div className="flex justify-between items-start pt-1 border-b border-gray-50 pb-3">
                  <span className="text-gray-400 font-bold text-sm shrink-0">주소</span>
                  <span className="text-gray-800 font-bold text-right text-sm leading-relaxed max-w-[200px] break-keep">
                    {shouldMaskTeacherInfo(item) ? (
                      <span className="blur-[3px] select-none text-gray-300">소중한 정보 보호 중 🔒</span>
                    ) : (
                      <AddressLink address={item.address} className="font-bold" />
                    )}
                  </span>
                </div>

                <div className="flex justify-between items-center pt-1">
                  <span className="text-gray-400 font-bold text-sm">신앙상태</span>
                  {shouldMaskTeacherInfo(item) ? (
                    <span className="text-gray-800 font-bold text-sm blur-[3px] select-none text-gray-300">
                      소중한 정보 보호 중 🔒
                    </span>
                  ) : (
                    <span
                      className={`font-bold px-2 py-0.5 rounded-md text-xs ${getFaithStateBadgeClass(item.faithState)}`}
                    >
                      {item.faithState || "-"}
                    </span>
                  )}
                </div>
              </div>

              {!shouldMaskTeacherInfo(item) && (
                <>
                  <div className="flex items-center justify-between pt-2">
                    <h3 className={`${MOBILE_DETAIL_SECTION_TITLE_CLASS} flex items-center gap-2`}>
                      <Heart size={14} className="fill-orange-400 text-orange-400" />
                      {editingLogId ? "심방 기록 수정" : "심방 일지"}
                    </h3>
                    {editingLogId && (
                      <button
                        onClick={() => {
                          setEditingLogId(null);
                          setLogInputContent("");
                          setLogInputType("면담");
                          setLogInputDate(new Date().toISOString().split("T")[0]);
                          setLogInputSentiment("neutral");
                        }}
                        className={MOBILE_DETAIL_CANCEL_EDIT_BUTTON_CLASS}
                      >
                        취소
                      </button>
                    )}
                  </div>

                  <div className={getMobileVisitEditorCardClass(Boolean(editingLogId))}>
                    <div className="flex gap-2 mb-3">
                      {["면담", "전화", "문자", "기도", "교제"].map((typeItem) => (
                        <button
                          key={typeItem}
                          onClick={() => setLogInputType(typeItem)}
                          className={getMobileVisitTypeButtonClass(logInputType, typeItem)}
                        >
                          {typeItem}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        id={`mobile-detail-visitlog-input-${item.id}`}
                        name={`mobile_detail_visitlog_input_${item.id}`}
                        type="text"
                        value={logInputContent}
                        onChange={(e) => setLogInputContent(e.target.value)}
                        placeholder="심방 내용을 간단히 기록..."
                        className={MOBILE_DETAIL_VISIT_EDITOR_INPUT_CLASS}
                      />
                      <button
                        onClick={() => {
                          if (!logInputContent.trim()) {
                            openAlert("내용을 입력해주세요.", "알림");
                            return;
                          }

                          if (editingLogId) {
                            if (onUpdateLog) {
                              const updatePayload = {
                                id: editingLogId,
                                type: logInputType,
                                content: logInputContent,
                                date: logInputDate,
                                sentiment: logInputSentiment,
                              };
                              if (isItemTeacher) {
                                updatePayload.teacherId = item.id;
                                updatePayload.teacherName = item.name;
                              } else {
                                updatePayload.studentId = item.id;
                                updatePayload.studentName = item.name;
                              }
                              onUpdateLog(updatePayload);
                              setLogInputContent("");
                              setEditingLogId(null);
                              setIsCreatingLog(false);
                              openAlert("심방 기록이 수정되었습니다! ✏️", "완료");
                            }
                          } else if (onAddLog) {
                            const newLog = {
                              type: logInputType,
                              content: logInputContent,
                              date: logInputDate,
                              sentiment: logInputSentiment,
                              createdAt: new Date().toISOString(),
                              createdBy: userProfile?.id || "unknown",
                              createdByName: userProfile?.name || "알 수 없음",
                            };

                            if (isItemTeacher) {
                              newLog.teacherId = item.id;
                              newLog.teacherName = item.name;
                            } else {
                              newLog.studentId = item.id;
                              newLog.studentName = item.name;
                            }

                            onAddLog(newLog);
                            setLogInputContent("");
                            openAlert("심방 기록이 저장되었습니다! 🙏", "완료");
                          }
                        }}
                        className={MOBILE_DETAIL_VISIT_EDITOR_SUBMIT_BUTTON_CLASS}
                      >
                        <Send size={16} /> {editingLogId ? "수정" : ""}
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const allStudentLogs = visitLogs
                      .filter((log) =>
                        isItemTeacher
                          ? log.teacherId === item.id || log.studentId === item.id
                          : log.studentId === item.id
                      )
                      .sort((a, b) => new Date(b.date) - new Date(a.date));

                    const displayLogs = showAllLogs ? allStudentLogs : allStudentLogs.slice(0, 5);
                    const hasMoreLogs = allStudentLogs.length > 5;

                    if (allStudentLogs.length === 0) {
                      return (
                        <div className={MOBILE_DETAIL_EMPTY_CARD_CLASS}>
                          <p className="text-gray-400 text-sm font-medium">아직 기록이 없어요</p>
                          <p className="text-gray-300 text-xs mt-1">위에서 첫 심방 기록을 남겨보세요!</p>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-2">
                        {displayLogs.map((log) => (
                          <div key={log.id} className={MOBILE_DETAIL_LOG_ITEM_CARD_CLASS}>
                            <div className="flex items-start gap-3">
                              <div className={getMobileVisitTypeIconWrapperClass(log.type)}>
                                <img
                                  src={getMobileVisitTypeIconSrc(log.type)}
                                  alt={log.type}
                                  className="w-6 h-6 object-contain drop-shadow-sm"
                                />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-gray-500">{log.type}</span>
                                    <span className="text-[10px] text-gray-400">{log.date}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingLogId(log.id);
                                        setLogInputContent(log.content || "");
                                        setLogInputType(log.type || "면담");
                                        setLogInputDate(
                                          log.date || new Date().toISOString().split("T")[0]
                                        );
                                        setLogInputSentiment(log.sentiment || "neutral");
                                        setIsCreatingLog(true);
                                      }}
                                      className={MOBILE_DETAIL_VISIT_LOG_ACTION_EDIT_BUTTON_CLASS}
                                    >
                                      <Edit size={14} />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openConfirm(
                                          "심방 기록 삭제",
                                          "이 기록을 삭제하시겠습니까?",
                                          () => {
                                            if (onDeleteLog) onDeleteLog(log.id);
                                          }
                                        );
                                      }}
                                      className={MOBILE_DETAIL_VISIT_LOG_ACTION_DELETE_BUTTON_CLASS}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                                <ExpandableText text={log.content || "내용 없음"} maxLines={2} />
                                {log.createdByName && (
                                  <p className="text-[10px] text-gray-400 mt-1">✍️ {log.createdByName}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                        {hasMoreLogs && (
                          <button
                            onClick={() => setShowAllLogs(!showAllLogs)}
                            className={MOBILE_DETAIL_VISIT_SHOW_MORE_BUTTON_CLASS}
                          >
                            {showAllLogs ? "접기 ▲" : `+ ${allStudentLogs.length - 5}개 더보기 ▼`}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}

              <div className={MOBILE_DETAIL_CARD_CLASS}>
                <h4 className={`${MOBILE_DETAIL_SUBTITLE_CLASS} mb-3 flex items-center gap-1`}>출석 기록</h4>
                <div
                  className={MOBILE_DETAIL_ACTION_ROW_CLASS}
                  onClick={() => onNavigateToAttendance && onNavigateToAttendance(item.lastAttendance)}
                >
                  <span className="text-gray-500 font-bold text-sm">최근 출석</span>
                  <span className="text-gray-900 font-bold flex items-center gap-1 text-sm">
                    {item.lastAttendance || "-"}
                    {item.lastAttendance && item.lastAttendance !== "-" && (
                      <ArrowRight size={12} className="text-gray-400" />
                    )}
                  </span>
                </div>
              </div>

              {item.notes && (
                <div className="bg-yellow-50/50 rounded-3xl p-5 shadow-sm border border-yellow-100/50">
                  <h4 className="text-xs font-black text-yellow-600/80 mb-2 flex items-center gap-1">
                    <StickyNote size={12} /> 비고
                  </h4>
                  <p className="text-sm font-medium text-gray-700 leading-relaxed">
                    {shouldMaskTeacherInfo(item) ? (
                      <span className="blur-[3px] select-none text-gray-300">소중한 정보 보호 중 🔒</span>
                    ) : (
                      item.notes
                    )}
                  </p>
                </div>
              )}

              <div className={[MOBILE_DETAIL_CARD_CLASS, "mt-4"].join(" ")}>
                <h4 className={`${MOBILE_DETAIL_SUBTITLE_CLASS} mb-4 flex items-center gap-1`}>
                  <Clock size={12} /> 사역 타임라인
                </h4>
                <div className="relative pl-4 border-l-2 border-gray-100 space-y-6">
                  <div className="relative">
                    <div className="absolute -left-[21px] top-0 w-3 h-3 rounded-full bg-[#354F38] ring-4 ring-white" />
                    <p className={MOBILE_DETAIL_TIMELINE_LABEL_CLASS}>등록일</p>
                    <p className="text-sm font-bold text-gray-800">
                      {shouldMaskTeacherInfo(item) ? (
                        <span className="blur-[3px] select-none text-gray-300">2024. 12. 25.</span>
                      ) : (
                        (() => {
                          const date = item.createdAt
                            ? item.createdAt.toDate
                              ? item.createdAt.toDate()
                              : new Date(item.createdAt)
                            : String(item.id).length >= 13
                              ? new Date(parseInt(item.id, 10))
                              : null;
                          return date && !Number.isNaN(date.getTime())
                            ? date.toLocaleDateString()
                            : "날짜 정보 없음";
                        })()
                      )}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      {shouldMaskTeacherInfo(item) ? (
                        <span className="blur-[3px] select-none text-gray-200">
                          우리 공동체와 함께한 시작일이에요 ✨
                        </span>
                      ) : (
                        "우리 공동체와 함께한 시작일이에요 ✨"
                      )}
                    </p>
                  </div>

                  <div className="relative">
                    <div className="absolute -left-[21px] top-0 w-3 h-3 rounded-full bg-blue-400 ring-4 ring-white" />
                    <p className={MOBILE_DETAIL_TIMELINE_LABEL_CLASS}>활동 요약</p>
                    <div className="flex gap-3 mt-2">
                      {shouldMaskTeacherInfo(item) ? (
                        <div className={MOBILE_DETAIL_ACTIVITY_CHIP_CLASS}>
                          <div className="w-2 h-2 rounded-full bg-gray-300" />
                          <span className="text-xs font-bold text-gray-300 blur-[3px] select-none">
                            상세 활동 내용이 가려져 있습니다
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className={MOBILE_DETAIL_ACTIVITY_CHIP_CLASS}>
                            <div className="w-2 h-2 rounded-full bg-green-500" />
                            <span className="text-xs font-bold text-gray-600">
                              출석 {attendanceLogs.filter((log) => log[item.id] === "present").length}회
                            </span>
                          </div>
                          <div className={MOBILE_DETAIL_ACTIVITY_CHIP_CLASS}>
                            <div className="w-2 h-2 rounded-full bg-orange-500" />
                            <span className="text-xs font-bold text-gray-600">
                              심방
                              {
                                visitLogs.filter((log) =>
                                  isItemTeacher
                                    ? log.teacherId === item.id || log.studentId === item.id
                                    : log.studentId === item.id
                                ).length
                              }
                              회
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
